import { redirect } from "react-router-dom";
import { authControllerMeV1Options } from "@/client/@tanstack/react-query.gen";
import { getDefaultPathForRole } from "./protected-loader";
import type { RoleSlug } from "./roles";
import { route, RouteKeys } from "@/services/routing/routes";
import queryClient from "@/services/react-query/query-client";

/**
 * Loader for the public landing page (`/`).
 *
 * Behaviour:
 * - If the visitor is authenticated AND has a role mapped to a non-home
 *   destination, redirect them to the role-appropriate dashboard (mirrors
 *   `guestLoader`'s behaviour for `/sign-in`, `/sign-up`, `/forgot-password`).
 * - Authenticated users with unmapped roles (e.g. PLATFORM_*) render the
 *   public landing page rather than being redirected back to `/`.
 * - Unauthenticated visitors render the public landing page.
 *
 * Loop guard: `getDefaultPathForRole` falls back to `RouteKeys.HOME` (`/`) for
 * any role not in its switch. Returning `redirect("/")` from this loader would
 * loop. Compare the resolved target against `/` and only redirect when it
 * actually leads somewhere else.
 */
export function homeLoader() {
  return async () => {
    try {
      const user = await queryClient.fetchQuery(authControllerMeV1Options());

      if (user) {
        const roleSlug = user.role?.slug as RoleSlug | undefined;
        if (roleSlug) {
          const target = getDefaultPathForRole(roleSlug);
          if (target !== route(RouteKeys.HOME)) {
            return redirect(target);
          }
        }
      }

      return null;
    } catch {
      // Unauthenticated or transient error — render the public landing page.
      return null;
    }
  };
}
